import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  BottomSheetContentEnum,
  type IChatInternalContext,
  ivrApi,
} from "@b2bportal/core-chat";
import {
  type ChatTransfer,
  EncodeGenericConversationResponseEnum,
} from "@b2bportal/ivr-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { noop } from "lodash-es";

const initChatContext: IChatInternalContext = {
  bottomSheetContent: BottomSheetContentEnum.Closed,
  chatProvider: null,
  closeChatWindow: noop,
  conversationId: "",
  getTripDetailsPath: () => "",
  minimizeChatWindow: noop,
  setBottomSheetContent: noop,
  setConversationId: noop,
  supportId: "",
};

export const ChatInternalContext =
  createContext<IChatInternalContext>(initChatContext);

export interface IChatInternalContextProps {
  chatContext: Partial<IChatInternalContext>;
  children: ReactNode;
}

export const ChatInternalContextProvider = (
  props: IChatInternalContextProps
) => {
  const { chatContext, children } = props;
  const { language: locale } = useI18nContext();
  const [bottomSheetContent, setBottomSheetContent] = useState(
    BottomSheetContentEnum.Closed
  );
  const [supportId, setSupportId] = useState("");

  const fetchSupportId = useCallback(
    async (chatTransfer: ChatTransfer) => {
      setSupportId("");

      try {
        // TODO Add params to fetchSupportId and call getBookingSupportId instead to make product-specific
        const { data: newSupportIdRes } =
          await ivrApi.apiV0IvrEncodeGenericPost({
            chatTransfer,
            locale,
            funnelType: "general",
            requestType: "general",
          });

        if (
          newSupportIdRes.EncodeGenericConversationResponse ===
          EncodeGenericConversationResponseEnum.Success
        ) {
          setSupportId(newSupportIdRes.encodedCode);
        }
      } catch {
        setSupportId("");
      }
    },
    [locale]
  );

  useEffect(() => {
    const { chatProvider, conversationId } = chatContext;

    if (chatProvider != null && conversationId != null) {
      const chatTransfer: ChatTransfer = {
        conversationId,
        source: chatProvider.chatTransferSource,
      };

      fetchSupportId(chatTransfer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatContext.chatProvider, chatContext.conversationId]);

  return (
    <ChatInternalContext.Provider
      value={{
        ...initChatContext,
        ...chatContext,
        bottomSheetContent,
        setBottomSheetContent,
        supportId,
      }}
    >
      {children}
    </ChatInternalContext.Provider>
  );
};
